import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

/**
 * CustomerEmailTemplatesEditor
 * ---------------------------------------------------------------------------
 * CRUD UI for the free-form "Email Customer" templates surfaced on the Job
 * Card. These are stored in `settings.email_templates.customer_templates` as
 * an array of {id, name, subject, body}.
 *
 * All mutations funnel through the parent's `onSave(nextTemplates)` handler so
 * the parent (SettingsPage) owns the single source of truth (`settings` state)
 * and the actual axios PUT. This component only handles the interaction model.
 *
 * IDs are generated client-side for new templates (`custom_<timestamp>`) so
 * the picker in JobDetail.jsx can keep a stable key even if the label is
 * renamed later. The 4 seeded defaults keep their meaningful slugs from the
 * backend (e.g. `additional_info_required`).
 */
export const CustomerEmailTemplatesEditor = ({ templates, onSave }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null = adding new
  const [draft, setDraft] = useState({ id: '', name: '', subject: '', body: '' });
  const [saving, setSaving] = useState(false);

  const openAddDialog = () => {
    setEditingIndex(null);
    setDraft({
      id: `custom_${Date.now()}`,
      name: '',
      subject: '',
      body: '',
      default_cc: '',
      default_bcc: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (index) => {
    setEditingIndex(index);
    // Backfill CC/BCC as empty strings for older templates that predate this
    // feature so the controlled inputs don't warn about switching to uncontrolled.
    const tpl = templates[index] || {};
    setDraft({
      default_cc: '',
      default_bcc: '',
      ...tpl,
    });
    setDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!draft.name.trim() || !draft.subject.trim() || !draft.body.trim()) {
      toast.error('Name, subject and body are all required');
      return;
    }
    setSaving(true);
    try {
      const next = [...templates];
      if (editingIndex === null) {
        next.push(draft);
      } else {
        next[editingIndex] = draft;
      }
      await onSave(next);
      toast.success(editingIndex === null ? 'Template added' : 'Template updated');
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed saving customer email template', err);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      const next = templates.filter((_, i) => i !== index);
      await onSave(next);
      toast.success('Template deleted');
    } catch (err) {
      console.error('Failed deleting customer email template', err);
      toast.error('Failed to delete template');
    }
  };

  return (
    <div className="template-section" style={{ marginTop: '1.5rem' }} data-testid="customer-email-templates-editor">
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <CardTitle style={{ fontSize: '1rem' }}>Customer Email Templates</CardTitle>
              <CardDescription>
                Reusable snippets for the <strong>Email Customer</strong> button on the Job Card. Placeholders: {'{customer_name}'}, {'{job_number}'}, {'{device_info}'}, {'{business_name}'}, {'{business_phone}'}, {'{business_email}'}. The job number is auto-prepended to the subject at send time.
              </CardDescription>
            </div>
            <Button
              onClick={openAddDialog}
              data-testid="add-customer-email-template-button"
              style={{ backgroundColor: '#10b981', color: 'white', whiteSpace: 'nowrap' }}
            >
              + Add Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(!templates || templates.length === 0) ? (
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
              No customer email templates yet. Click <strong>Add Template</strong> to create your first one.
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {templates.map((tpl, index) => (
                <div
                  key={tpl.id || index}
                  data-testid={`customer-email-template-row-${index}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '0.75rem 1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.15rem' }}>{tpl.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '0.15rem' }}>
                      <em>Subject:</em> {tpl.subject}
                    </div>
                    {(tpl.default_cc || tpl.default_bcc) && (
                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.15rem' }}>
                        {tpl.default_cc && (<><em>CC:</em> {tpl.default_cc}{tpl.default_bcc ? ' · ' : ''}</>)}
                        {tpl.default_bcc && (<><em>BCC:</em> {tpl.default_bcc}</>)}
                      </div>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'pre-line', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {tpl.body}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(index)}
                      data-testid={`edit-customer-email-template-${index}`}
                    >
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          style={{ color: '#dc2626', borderColor: '#fecaca' }}
                          data-testid={`delete-customer-email-template-${index}`}
                        >
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete &quot;{tpl.name}&quot;?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This template will be removed permanently. This won&apos;t affect emails that have already been sent.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(index)}
                            style={{ backgroundColor: '#dc2626' }}
                            data-testid={`confirm-delete-customer-email-template-${index}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent style={{ maxWidth: '650px', maxHeight: '85vh', overflow: 'auto' }} data-testid="customer-email-template-dialog">
          <DialogHeader>
            <DialogTitle>{editingIndex === null ? 'Add Email Template' : 'Edit Email Template'}</DialogTitle>
            <DialogDescription>
              Use placeholders like <code>{'{customer_name}'}</code>, <code>{'{job_number}'}</code>, <code>{'{device_info}'}</code>. The job number is auto-prefixed to the subject when sent.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div>
              <Label htmlFor="tpl-name">Template Name</Label>
              <Input
                id="tpl-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Awaiting Password"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tpl-subject">Subject</Label>
              <Input
                id="tpl-subject"
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                placeholder="e.g. Additional information required"
                data-testid="template-subject-input"
              />
              <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                Do NOT include the job number here — it&apos;s added automatically as <code>[Job #XXXX]</code>.
              </p>
            </div>
            <div>
              <Label htmlFor="tpl-body">Body</Label>
              <Textarea
                id="tpl-body"
                rows={12}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder={'Hi {customer_name},\n\n... your message ...\n\nKind regards,\n{business_name}'}
                data-testid="template-body-input"
                style={{ fontFamily: 'inherit', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <Label htmlFor="tpl-default-cc">Default CC (comma-separated)</Label>
                <Input
                  id="tpl-default-cc"
                  value={draft.default_cc || ''}
                  onChange={(e) => setDraft({ ...draft, default_cc: e.target.value })}
                  placeholder="you@yourbusiness.com"
                  data-testid="template-default-cc-input"
                />
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                  Auto-fills the CC field when this template is picked. Great for self-archiving.
                </p>
              </div>
              <div>
                <Label htmlFor="tpl-default-bcc">Default BCC (comma-separated)</Label>
                <Input
                  id="tpl-default-bcc"
                  value={draft.default_bcc || ''}
                  onChange={(e) => setDraft({ ...draft, default_bcc: e.target.value })}
                  placeholder="accountant@example.com"
                  data-testid="template-default-bcc-input"
                />
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                  Hidden recipient. The customer can&apos;t see this.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              data-testid="save-customer-email-template-button"
            >
              {saving ? 'Saving…' : (editingIndex === null ? 'Add Template' : 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
